import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import vertSrc from './stripe-wave/wave.vert.glsl?raw';
import fragSrc from './stripe-wave/wave.frag.glsl?raw';

/**
 * Faithful port of Stripe's developers-wave-animation: folded plane mesh with
 * twist + simplex-noise displacement, palette-LUT colouring, animated over
 * time. Shader source extracted from stripe.com bundle.
 *
 * First pass: mesh + shaders only, no post-processing.
 */

// Folded-plane geometry: a PlaneGeometry whose vertices are bent into a fold
// (a curved tube-like shape). Ported from the bundle.
function buildFoldedGeometry(width = 400, height = 400, subX = 128, subY = 256): THREE.BufferGeometry {
  const expCurve = (e: number, t: number) => Math.pow(4 * e * (1 - e), t);
  const xAxis = new THREE.Vector3(1, 0, 0);
  const yAxis = new THREE.Vector3(0, 1, 0);

  const geo = new THREE.PlaneGeometry(width, height, subX, subY);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const uv = geo.attributes.uv as THREE.BufferAttribute;

  for (let i = 0; i < pos.count; i++) {
    const p = new THREE.Vector3().fromBufferAttribute(pos, i);
    const u = new THREE.Vector2().fromBufferAttribute(uv, i);
    const r = 4 - 2 * expCurve(u.y, 9.5);
    if (p.x < -16) {
      p.z += r;
    } else if (p.x < 16) {
      p.z = Math.cos(THREE.MathUtils.mapLinear(p.x, -16, 16, 0, Math.PI)) * r;
      p.x = Math.cos(THREE.MathUtils.mapLinear(p.x, -16, 16, -Math.PI / 2, Math.PI / 2)) * r - 16;
    } else {
      p.z -= r;
      p.x = -p.x;
    }
    p.x += width / 4;
    p.applyAxisAngle(xAxis, -Math.PI / 2);
    p.applyAxisAngle(yAxis, -Math.PI / 2);
    pos.setXYZ(i, p.x, p.y, p.z);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

// Extracted from the QR (developers-wave-animation, dark theme) config in the
// stripe.com bundle.
const QR = {
  theme: 'dark' as const,
  camState: {
    position: { x: 100, y: 0, z: 5000 },
    rotation: { x: 0, y: 0.019997333973150535, z: 0 },
    lookat: { x: 0, y: 0, z: 0 },
    zoom: 1,
  },
  postProcessing: { blurAmount: 0.02, grainAmount: 1.2 },
  materialProps: {
    speed: 4e-5,
    timeOffset: 1150,
    colorContrast: 1,
    colorSaturation: 1.15,
    colorHueShift: -0.0315926535897932,
    displaceFrequencyX: 0.003234,
    displaceFrequencyZ: 0.00799,
    displaceAmount: 6.051,
    positionX: -24.3,
    positionY: -56.4,
    positionZ: -11.0999999999999,
    rotationX: -0.159592653589793,
    rotationY: -0.283592653589793,
    rotationZ: -2.81559265358979,
    scaleX: 10,
    scaleY: 10,
    scaleZ: 7,
    twistFrequencyX: -0.0549999999999997,
    twistFrequencyY: 0.077,
    twistFrequencyZ: -0.518,
    twistPowerX: 3.95,
    twistPowerY: 5.85,
    twistPowerZ: 6.33,
    glowRamp: 1,
    glowAmount: 0.6,
    glowPower: 0.589,
    lineThickness: 1,
    lineAmount: 425,
    lineDerivativePower: 0.95,
  },
};

// Generate a 256×1 palette texture using only light colours (no dark stops).
function makePaletteTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 1;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 256, 0);
  // Single consistent blue, very pale — mostly white with a soft blue tint.
  g.addColorStop(0.0, '#dbeafe');   // blue-100
  g.addColorStop(0.6, '#dbeafe');   // blue-100 (hold the same hue)
  g.addColorStop(1.0, '#ffffff');   // white highlight
  // Original pastel rainbow palette — keep for easy revert:
  // g.addColorStop(0.0, '#dbeafe');   // pale blue
  // g.addColorStop(0.2, '#e0e7ff');   // pale indigo
  // g.addColorStop(0.4, '#fce7f3');   // pale pink
  // g.addColorStop(0.6, '#fef3c7');   // pale amber
  // g.addColorStop(0.8, '#ccfbf1');   // pale teal
  // g.addColorStop(1.0, '#ffffff');   // white
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 1);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  return tex;
}

// Strip the `attribute vec3 tangent` line since Three.js doesn't supply it and
// the shader doesn't actually use it.
const VERTEX_SHADER = vertSrc.replace(/attribute\s+vec3\s+tangent\s*;\s*/g, '');
const FRAGMENT_SHADER = fragSrc;

interface StripeWaveProps {
  height?: number;
  className?: string;
  /** RGB 0..1 for the background between wave lines. Default light grey. */
  background?: [number, number, number];
}

export function StripeWave({ height = 460, className, background = [0.96, 0.96, 0.97] }: StripeWaveProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(dpr);

    let width = container.clientWidth || 800;
    renderer.setSize(width, height, false);
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-width / 2, width / 2, height / 2, -height / 2, 0.1, 100000);
    camera.position.set(QR.camState.position.x, QR.camState.position.y, QR.camState.position.z);
    camera.rotation.set(QR.camState.rotation.x, QR.camState.rotation.y, QR.camState.rotation.z);
    camera.lookAt(QR.camState.lookat.x, QR.camState.lookat.y, QR.camState.lookat.z);
    camera.zoom = QR.camState.zoom;
    camera.updateProjectionMatrix();

    const paletteTex = makePaletteTexture();

    const m = QR.materialProps;
    const uniforms: Record<string, THREE.IUniform> = {
      u_time: { value: m.timeOffset },
      u_speed: { value: m.speed },
      u_resolution: { value: new THREE.Vector2(width * dpr, height * dpr) },
      u_paletteTexture: { value: paletteTex },
      u_lutTexture: { value: paletteTex },
      u_blueNoiseTexture: { value: paletteTex },
      u_colorContrast: { value: m.colorContrast },
      u_colorSaturation: { value: m.colorSaturation },
      u_colorHueShift: { value: m.colorHueShift },
      u_displaceFrequencyX: { value: m.displaceFrequencyX },
      u_displaceFrequencyZ: { value: m.displaceFrequencyZ },
      u_displaceAmount: { value: m.displaceAmount },
      u_twistFrequencyX: { value: m.twistFrequencyX },
      u_twistFrequencyY: { value: m.twistFrequencyY },
      u_twistFrequencyZ: { value: m.twistFrequencyZ },
      u_twistPowerX: { value: m.twistPowerX },
      u_twistPowerY: { value: m.twistPowerY },
      u_twistPowerZ: { value: m.twistPowerZ },
      u_glowAmount: { value: m.glowAmount },
      u_glowPower: { value: m.glowPower },
      u_glowRamp: { value: m.glowRamp },
      u_mousePosition: { value: new THREE.Vector2(0, 0) },
      u_lineAmount: { value: m.lineAmount },
      u_lineThickness: { value: m.lineThickness },
      u_lineDerivativePower: { value: m.lineDerivativePower },
      u_maxWidth: { value: width },
      // Background colour the shader mixes into between lines.
      u_clearColor: { value: new THREE.Color(background[0], background[1], background[2]) },
    };

    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms,
      transparent: true,
      side: THREE.DoubleSide,
    });

    const geometry = buildFoldedGeometry();
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(m.positionX, m.positionY, m.positionZ);
    mesh.rotation.set(m.rotationX, m.rotationY, m.rotationZ);
    mesh.scale.set(m.scaleX, m.scaleY, m.scaleZ);
    scene.add(mesh);

    const resize = () => {
      const w = container.clientWidth;
      if (!w || w === width) return;
      width = w;
      renderer.setSize(width, height, false);
      camera.left = -width / 2;
      camera.right = width / 2;
      camera.updateProjectionMatrix();
      uniforms.u_resolution.value.set(width * dpr, height * dpr);
      uniforms.u_maxWidth.value = width;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const start = performance.now();
    let rafId = 0;
    let frame = 0;
    let isVisible = false;
    // Stripe targets 30fps for this wave — same here, smoother feel + ~halves GPU cost.
    const FRAME_INTERVAL = 2;
    const tick = () => {
      if (!isVisible) {
        rafId = 0;
        return;
      }
      rafId = requestAnimationFrame(tick);
      frame += 1;
      if (frame % FRAME_INTERVAL !== 0) return;
      const elapsed = performance.now() - start;
      uniforms.u_time.value = m.timeOffset + elapsed;
      renderer.render(scene, camera);
    };

    const io = new IntersectionObserver(
      (entries) => {
        const nowVisible = entries[0]?.isIntersecting ?? false;
        if (nowVisible === isVisible) return;
        isVisible = nowVisible;
        if (isVisible && !rafId) rafId = requestAnimationFrame(tick);
      },
      { rootMargin: '200px 0px' },
    );
    io.observe(container);

    return () => {
      io.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      geometry.dispose();
      material.dispose();
      paletteTex.dispose();
      renderer.dispose();
    };
    // `background` deliberately omitted from deps — it's an array literal so a
    // fresh reference each render would tear down and rebuild the whole scene,
    // which restarts the animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: '100%',
        height,
        // Fade the canvas on all four sides so the wave dissolves into the
        // surrounding background — same look as stripe.com. Two gradients
        // (horizontal + vertical) intersected so corners fade too.
        WebkitMaskImage:
          'linear-gradient(to right, transparent 0%, #000 20%, #000 80%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 15%, #000 85%, transparent 100%)',
        WebkitMaskComposite: 'source-in',
        maskImage:
          'linear-gradient(to right, transparent 0%, #000 20%, #000 80%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 15%, #000 85%, transparent 100%)',
        maskComposite: 'intersect',
      }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
}
